import { CheckCircleIcon } from "@chakra-ui/icons"
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Link,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react"
import type { Dispatch, SetStateAction } from "react"
import { type FC, useState } from "react"
import CardContainer from "@/common/components/CardContainer"
import { EXTERNAL_ROUTES, ROUTES } from "@/common/constants/routes"
import AssistedLaunchModal from "@/organization/components/onboarding/AssistedLaunchModal"
import ArrowRight from "@/ui/components/icons/ArrowRight"
import CircleQuestion from "@/ui/components/icons/CircleQuestion"
import GigaBrain from "@/ui/components/icons/custom/GigaBrain"
import Hand from "@/ui/components/icons/custom/Hand"

const OrganizationToggleLaunch: FC = () => {
  const [selectedOption, setSelectedOption] = useState<"assistance" | "deploy">(
    "assistance",
  )
  const [showSelfCheck, setShowSelfCheck] = useState(false)
  const [checked1, setChecked1] = useState(false)
  const [checked2, setChecked2] = useState(false)
  const [checked3, setChecked3] = useState(false)

  const assistedLaunchModal = useDisclosure()

  const shouldShowAssistance = selectedOption === "assistance"
  const shouldShowSelfServe = selectedOption === "deploy"

  const isDisabled = !checked1 || !checked2 || !checked3

  if (showSelfCheck) {
    return (
      <CardContainer
        isTableView
        mt={{ base: 6, sm: 12 }}
        title="Add a DAO to Tally "
        topRight={
          <Link isExternal href={EXTERNAL_ROUTES.tally.docs.deploymentGuide()}>
            <HStack w="200px">
              <Text>See deployment guide</Text>
              <Icon as={CircleQuestion} fill="gray.500" />
            </HStack>
          </Link>
        }
      >
        <Stack px={5} py={4} spacing={6}>
          <Text color="gray.900" textStyle="body.bold.lg">
            Make sure to finish the steps below so everything&apos;s set to go!
          </Text>
          <HStack
            borderColor="gray.200"
            borderRadius="md"
            borderWidth="1px"
            justify="space-between"
            p={4}
          >
            <HStack align="center">
              <Checkbox
                colorScheme="purple"
                isChecked={checked1}
                onChange={(e) => setChecked1(e.target.checked)}
              />
              <Text color="gray.700">Review Tally compatible requirements</Text>
            </HStack>
            <Link href={EXTERNAL_ROUTES.tally.docs.openZeppelinGovernor()}>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </Link>
          </HStack>
          <HStack
            borderColor="gray.200"
            borderRadius="md"
            borderWidth="1px"
            justify="space-between"
            p={4}
          >
            <HStack align="center">
              <Checkbox
                colorScheme="purple"
                isChecked={checked2}
                onChange={(e) => setChecked2(e.target.checked)}
              />
              <Text color="gray.700">Deploy a governor contract</Text>
            </HStack>
            <Link href={EXTERNAL_ROUTES.tally.docs.deployGovernor()}>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </Link>
          </HStack>
          <HStack
            borderColor="gray.200"
            borderRadius="md"
            borderWidth="1px"
            justify="space-between"
            p={4}
          >
            <HStack align="center">
              <Checkbox
                colorScheme="purple"
                isChecked={checked3}
                onChange={(e) => setChecked3(e.target.checked)}
              />
              <Text color="gray.700">Deploy a token contract</Text>
            </HStack>
            <Link href={EXTERNAL_ROUTES.tally.docs.deployGovernanceToken()}>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </Link>
          </HStack>
          {isDisabled ? (
            <Button isDisabled variant="primary" w="120px">
              Get Started
            </Button>
          ) : (
            <Button
              as={Link}
              className="no-underline"
              href={ROUTES.getStarted.governor()}
              variant="primary"
              w="120px"
            >
              Get Started
            </Button>
          )}
        </Stack>
      </CardContainer>
    )
  }

  return (
    <Stack fontSize={{ base: 4, lg: 24 }} my={{ base: 4, lg: 24 }} w="100%">
      <Stack align="center">
        <Text
          as="h3"
          bgClip="text"
          bgGradient="linear(to-r, #725BFF, #6E40CF)"
          fontFamily="Plus Jakarta Sans"
          fontSize={{ base: "31px", lg: "44px" }}
          fontWeight={700}
          lineHeight={{ base: "40px", lg: "70px" }}
          textAlign="center"
        >
          Launch your governance on Tally
        </Text>
        <Text
          fontSize={{ base: "18px", lg: "24px" }}
          lineHeight={{ base: "28px", lg: "30px" }}
          px={{ base: 6, lg: 0 }}
          textAlign="center"
          textStyle="body.regular.md"
        >
          Easily deploy governance contracts & integrate your DAO with Tally
        </Text>
        <ButtonGroup
          isAttached
          mb={{ base: 6, lg: 10 }}
          mt={{ base: 6, lg: 10 }}
          variant="outline"
        >
          <Button
            fontWeight="400"
            isActive={selectedOption === "assistance"}
            textStyle="body.regularsmd"
            onClick={() => setSelectedOption("assistance")}
          >
            Get assistance
          </Button>
          <Button
            fontWeight="400"
            isActive={selectedOption === "deploy"}
            textStyle="body.regular.sm"
            onClick={() => setSelectedOption("deploy")}
          >
            Deploy myself
          </Button>
        </ButtonGroup>
        {shouldShowAssistance ? (
          <AssistedLaunch onOpen={assistedLaunchModal.onOpen} />
        ) : null}
        {shouldShowSelfServe ? (
          <SelfServeLaunch setShowSelfCheck={setShowSelfCheck} />
        ) : null}
      </Stack>
      <AssistedLaunchModal
        isOpen={assistedLaunchModal.isOpen}
        onClose={assistedLaunchModal.onClose}
      />
    </Stack>
  )
}

function AssistedLaunch({ onOpen }: { onOpen: () => void }) {
  return (
    <Box
      bg="white"
      borderColor="#D2C7F3"
      borderRadius="md"
      borderWidth="1px"
      p={{ base: 3, lg: 6 }}
      width={{ base: "95%", lg: "90%" }}
    >
      <Flex
        direction={{ base: "column-reverse", lg: "row" }}
        justify="space-between"
      >
        <Stack spacing={3}>
          <Text
            color="gray.700"
            fontSize="20px"
            fontWeight="700"
            lineHeight={{ base: "28px", lg: "28px" }}
            textStyle="body.bold.md"
          >
            Work with Tally to launch governance
          </Text>
          <Text
            color="gray.600"
            fontSize={{ base: "16px", lg: "18px" }}
            fontWeight="400"
            textStyle="body.bold.md"
          >
            Information you will need to have handy
          </Text>
          <Stack my={3} spacing={5}>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Text
                color="gray.600"
                fontSize={{ base: "15px", lg: "16px" }}
                fontWeight="400"
                textStyle="body.bold.md"
              >
                Your contact information
              </Text>
            </HStack>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Text
                color="gray.600"
                fontSize={{ base: "15px", lg: "16px" }}
                fontWeight="400"
                textStyle="body.bold.md"
              >
                What project you are working on?
              </Text>
            </HStack>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Text
                color="gray.600"
                fontSize={{ base: "15px", lg: "16px" }}
                fontWeight="400"
                textStyle="body.bold.md"
              >
                How soon you want to launch governance?
              </Text>
            </HStack>
          </Stack>
          <Button
            variant="primary"
            width={{ base: "full", lg: "240px" }}
            onClick={onOpen}
          >
            Launch with Tally&apos;s Assistance
          </Button>
        </Stack>
        <Flex width={{ base: "100%", lg: "40%" }}>
          <Box
            _before={{
              content: '""',
              display: "block",
              paddingBottom: "60.82%", // (208 / 342) * 100 = 60.82%
            }}
            alignItems="center"
            bg="rgba(244, 240, 255, 0.5)"
            display="flex"
            justifyContent="center"
            mb={{ base: 12, lg: "4%" }}
            ml={{ base: 10, lg: 0 }}
            mr={{ base: 10, lg: "15%" }}
            mt={{ base: 8, lg: "4%" }}
            position="relative"
            w="100%"
          >
            <Icon as={Hand} h="115%" position="absolute" top="-6%" w="78%" />
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

const SelfServeLaunch: FC<{
  setShowSelfCheck: Dispatch<SetStateAction<boolean>>
}> = ({ setShowSelfCheck }) => {
  return (
    <Box
      bg="white"
      borderColor="#D2C7F3"
      borderRadius="md"
      borderWidth="1px"
      p={{ base: 3, lg: 6 }}
      width={{ base: "95%", lg: "90%" }}
    >
      <Flex
        direction={{ base: "column-reverse", lg: "row" }}
        justify="space-between"
      >
        <Stack spacing={3}>
          <Text
            color="gray.700"
            fontSize="20px"
            fontWeight="700"
            lineHeight={{ base: "28px", lg: "28px" }}
            textStyle="body.bold.md"
          >
            Get ready to add a Governor contract
          </Text>
          <Text
            color="gray.600"
            fontSize={{ base: "16px", lg: "18px" }}
            fontWeight="400"
            textStyle="body.bold.md"
          >
            Let&apos;s get you all set up
          </Text>
          <Stack my={3} spacing={5}>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Link href="https://docs.tally.xyz/user-guides/tally-contract-compatibility">
                <Text
                  color="gray.600"
                  fontSize={{ base: "15px", lg: "16px" }}
                  fontWeight="400"
                  textStyle="body.bold.md"
                >
                  Review Tally Compatible Requirements
                </Text>
              </Link>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </HStack>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Link href="https://docs.tally.xyz/user-guides/deploying-governor-daos/deploy-a-governor">
                <Text
                  color="gray.600"
                  fontSize={{ base: "15px", lg: "16px" }}
                  fontWeight="400"
                  textStyle="body.bold.md"
                >
                  Deploy a governor contract
                </Text>
              </Link>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </HStack>
            <HStack spacing={3}>
              <Icon as={CheckCircleIcon} color="gray.400" h={5} w={5} />
              <Link href="https://docs.tally.xyz/user-guides/deploying-governor-daos/deploy-a-governance-token">
                <Text
                  color="gray.600"
                  fontSize={{ base: "15px", lg: "16px" }}
                  fontWeight="400"
                  textStyle="body.bold.md"
                >
                  Deploy a token contract
                </Text>
              </Link>
              <Icon as={ArrowRight} fill="purple.500" h={5} w={5} />
            </HStack>
          </Stack>
          <Button
            className="no-underline"
            variant="primary"
            width={{ base: "full", lg: "240px" }}
            onClick={() => setShowSelfCheck(true)}
          >
            Deploy contracts yourself
          </Button>
        </Stack>
        <Flex width={{ base: "100%", lg: "40%" }}>
          <Box
            _before={{
              content: '""',
              display: "block",
              paddingBottom: "60.82%", // (208 / 342) * 100 = 60.82%
            }}
            alignItems="center"
            bg="rgba(244, 240, 255, 0.5)"
            display="flex"
            justifyContent="center"
            mb={{ base: 12, lg: "4%" }}
            ml={{ base: 10, lg: 0 }}
            mr={{ base: 10, lg: "15%" }}
            mt={{ base: 8, lg: "4%" }}
            position="relative"
            w="100%"
          >
            <Icon
              as={GigaBrain}
              h="115%"
              position="absolute"
              top="-8%"
              w="75%"
            />
          </Box>
        </Flex>
      </Flex>
    </Box>
  )
}

export default OrganizationToggleLaunch
